import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Button, Col, Form, Nav, Row, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ApiService from "../services/ApiService";
import { TemplateFile } from "../models/Templates";
import { TemplateFieldMeta, TemplateMeta, QrCodeSpec } from "../models/TemplateMeta";
import logger from "../utils/logging";
import { extractPdfFieldWidgets, PdfFieldWidget } from "../utils/pdfFields";
import TemplatePreviewPane from "../components/templateEditor/PdfPreviewPane";
import ToastSuccess from "../components/ToastSuccess";
import ToastWarning from "../components/ToastWarning";
import "../css/TemplateEditor.css";

type TemplateEditorProps = {
    apiService: ApiService;
};

const createQrId = () => {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
        return crypto.randomUUID();
    }
    return `qr-${Math.random().toString(36).slice(2, 10)}`;
};

const createDefaultQr = (): QrCodeSpec => ({
    id: createQrId(),
    content: "{{guest.ticketId}}",
    position: { type: "coordinates", page: 1, x: 60, y: 120, origin: "top-left" },
    size: 120,
    color: "#000000",
    backgroundColor: "#ffffff",
});

const MIN_QR_SIZE = 40;
const MAX_QR_SIZE = 400;

const mergeTemplateFields = (template: TemplateFile, metaFields: TemplateFieldMeta[]): TemplateFieldMeta[] => {
    const existing = new Map(metaFields.map((field) => [field.name, field]));
    return template.fields.map((field) => ({
        name: field.name,
        alternativeName: existing.get(field.name)?.alternativeName ?? field.alternativeName ?? "",
        defaultValue: existing.get(field.name)?.defaultValue ?? "",
    }));
};

const getMetaSnapshot = (input: TemplateMeta): string =>
    JSON.stringify({
        name: input.name,
        fields: input.fields.map((field) => ({
            name: field.name,
            alternativeName: field.alternativeName ?? "",
            defaultValue: field.defaultValue ?? "",
        })),
        qrCodes: input.qrCodes.map((qr) => ({
            id: qr.id,
            content: qr.content ?? "",
            position: {
                type: qr.position.type,
                page: qr.position.page,
                x: qr.position.x,
                y: qr.position.y,
                origin: qr.position.origin ?? "top-left",
            },
            size: qr.size ?? 0,
            color: qr.color ?? "#000000",
            backgroundColor: qr.backgroundColor ?? "#ffffff",
        })),
    });

const TemplateEditor: React.FC<TemplateEditorProps> = ({ apiService }) => {
    const { t } = useTranslation(["templates", "common", "pdfPreview"]);
    const { id } = useParams();
    const navigate = useNavigate();

    const [template, setTemplate] = React.useState<TemplateFile | null>(null);
    const [pdfBytes, setPdfBytes] = React.useState<ArrayBuffer | null>(null);
    const [templatePdfBlobUrl, setTemplatePdfBlobUrl] = React.useState<string | null>(null);
    const [fieldWidgets, setFieldWidgets] = React.useState<PdfFieldWidget[]>([]);
    const [meta, setMeta] = React.useState<TemplateMeta | null>(null);
    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [activeTab, setActiveTab] = React.useState<"fields" | "qr" | "info">("fields");
    const [selectedQrId, setSelectedQrId] = React.useState<string | null>(null);
    const [selectedFieldName, setSelectedFieldName] = React.useState<string | null>(null);
    const [hoveredFieldName, setHoveredFieldName] = React.useState<string | null>(null);
    const [showFieldNames, setShowFieldNames] = React.useState(true);
    const [showQrCodes, setShowQrCodes] = React.useState(true);
    const [previewScale, setPreviewScale] = React.useState(1);
    const [pageCount, setPageCount] = React.useState<number>(1);
    const [pageSizes, setPageSizes] = React.useState<Record<number, { width: number; height: number }>>({});
    const [qrSizeDrafts, setQrSizeDrafts] = React.useState<Record<string, string>>({});
    const [savedSnapshot, setSavedSnapshot] = React.useState<string | null>(null);
    const [successToastMessage, setSuccessToastMessage] = React.useState<string | null>(null);
    const [errorToastMessage, setErrorToastMessage] = React.useState<string | null>(null);
    const fieldCardRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

    React.useEffect(() => {
        if (!id) {
            setError(t("editor.errors.missingTemplate"));
            return;
        }

        let cancelled = false;

        const loadTemplate = async () => {
            setIsLoading(true);
            try {
                const templates = await apiService.getTemplates();
                const found = templates.find((item) => item.id === id) ?? null;
                if (!cancelled) {
                    setTemplate(found);
                    if (!found) {
                        setError(t("editor.errors.notFound"));
                    }
                }
            } catch (err) {
                if (!cancelled) {
                    setError(t("editor.errors.load"));
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };

        void loadTemplate();

        return () => {
            cancelled = true;
        };
    }, [apiService, id, t]);

    React.useEffect(() => {
        if (!template) {
            setPdfBytes(null);
            setFieldWidgets([]);
            return;
        }

        let cancelled = false;

        const loadPdf = async () => {
            try {
                const blob = await apiService.getTemplatePdf(template.id);
                const buffer = await blob.arrayBuffer();
                if (!cancelled) {
                    setPdfBytes(buffer);
                    const widgets = await extractPdfFieldWidgets(buffer);
                    setFieldWidgets(widgets);
                }
            } catch (err) {
                logger.error("Failed to load template PDF", err);
                if (!cancelled) {
                    setError(t("editor.errors.loadPdf"));
                }
            }
        };

        void loadPdf();

        return () => {
            cancelled = true;
        };
    }, [apiService, template, t]);

    React.useEffect(() => {
        if (!pdfBytes) {
            setTemplatePdfBlobUrl(null);
            return;
        }

        const blobUrl = URL.createObjectURL(new Blob([pdfBytes.slice(0)], { type: "application/pdf" }));
        setTemplatePdfBlobUrl(blobUrl);

        return () => {
            URL.revokeObjectURL(blobUrl);
        };
    }, [pdfBytes]);

    React.useEffect(() => {
        if (!template) {
            setMeta(null);
            setSavedSnapshot(null);
            return;
        }

        let cancelled = false;

        const loadMeta = async () => {
            try {
                const storedMeta = await apiService.getTemplateMeta(template.id);
                if (cancelled) {
                    return;
                }
                const nextMeta = {
                    id: template.id,
                    name: storedMeta.name ?? template.name,
                    fields: mergeTemplateFields(template, storedMeta.fields),
                    qrCodes: storedMeta.qrCodes,
                    updatedAt: storedMeta.updatedAt ?? null,
                };
                setMeta(nextMeta);
                setSavedSnapshot(getMetaSnapshot(nextMeta));
            } catch (err) {
                logger.error("Failed to load template metadata", err);
                if (!cancelled) {
                    const fallbackMeta = {
                        id: template.id,
                        name: template.name,
                        fields: mergeTemplateFields(template, []),
                        qrCodes: [],
                        updatedAt: null,
                    };
                    setMeta(fallbackMeta);
                    setSavedSnapshot(getMetaSnapshot(fallbackMeta));
                }
            }
        };

        void loadMeta();

        return () => {
            cancelled = true;
        };
    }, [apiService, template]);

    const updateField = (name: string, patch: Partial<TemplateFieldMeta>) => {
        setMeta((current) => {
            if (!current) return current;
            return {
                ...current,
                fields: current.fields.map((field) => (field.name === name ? { ...field, ...patch } : field)),
            };
        });
    };

    const updateTemplateName = (name: string) => {
        setMeta((current) => {
            if (!current) return current;
            return {
                ...current,
                name,
            };
        });
    };

    const clampQrPosition = (
        page: number,
        size: number,
        position: { x: number; y: number },
    ): { x: number; y: number } => {
        const pageSize = pageSizes[page];
        if (!pageSize) {
            return position;
        }

        const maxX = Math.max(0, pageSize.width - size);
        const maxY = Math.max(0, pageSize.height - size);

        const clampedX = Math.min(Math.max(0, position.x), maxX);
        const clampedY = Math.min(Math.max(0, position.y), maxY);

        return {
            x: Math.floor(clampedX),
            y: Math.floor(clampedY),
        };
    };

    const normalizeQrSize = (value: number) => {
        if (!Number.isFinite(value)) {
            return MIN_QR_SIZE;
        }
        return Math.min(Math.max(value, MIN_QR_SIZE), MAX_QR_SIZE);
    };

    const handlePageMetrics = React.useCallback(
        (pageIndex: number, metrics: { width: number; height: number }) => {
            setPageSizes((current) => {
                const pageNumber = pageIndex + 1;
                const existing = current[pageNumber];
                if (existing && existing.width === metrics.width && existing.height === metrics.height) {
                    return current;
                }
                return {
                    ...current,
                    [pageNumber]: metrics,
                };
            });
        },
        [],
    );


    const getFieldPages = React.useCallback(
        (fieldName: string) => {
            const pages = new Set<number>();
            fieldWidgets.forEach((widget) => {
                if (widget.name === fieldName) {
                    pages.add(widget.pageIndex + 1);
                }
            });
            return Array.from(pages).sort((a, b) => a - b);
        },
        [fieldWidgets],
    );

    const updateQr = (id: string, patch: Partial<QrCodeSpec>) => {
        setMeta((current) => {
            if (!current) return current;
            return {
                ...current,
                qrCodes: current.qrCodes.map((qr) => (qr.id === id ? { ...qr, ...patch } : qr)),
            };
        });
    };

    const updateQrFromDrag = (id: string, update: { x: number; y: number; size: number }) => {
        setMeta((current) => {
            if (!current) return current;
            return {
                ...current,
                qrCodes: current.qrCodes.map((qr) => {
                    if (qr.id !== id) return qr;
                    if (qr.position.type !== "coordinates") return qr;
                    const size = normalizeQrSize(update.size);
                    const clamped = clampQrPosition(qr.position.page, size, {
                        x: update.x,
                        y: update.y,
                    });
                    return {
                        ...qr,
                        position: {
                            ...qr.position,
                            x: clamped.x,
                            y: clamped.y,
                            origin: qr.position.origin ?? "top-left",
                        },
                        size,
                    };
                }),
            };
        });
    };

    const updateQrPosition = (id: string, patch: Partial<QrCodeSpec["position"]>) => {
        setMeta((current) => {
            if (!current) return current;
            return {
                ...current,
                qrCodes: current.qrCodes.map((qr) => {
                    if (qr.id !== id) return qr;
                    const nextPosition = { ...qr.position, ...patch } as QrCodeSpec["position"];
                    if (nextPosition.type === "coordinates") {
                        const size = normalizeQrSize(qr.size ?? 120);
                        const clamped = clampQrPosition(nextPosition.page, size, {
                            x: nextPosition.x,
                            y: nextPosition.y,
                        });
                        return {
                            ...qr,
                            position: {
                                ...nextPosition,
                                x: clamped.x,
                                y: clamped.y,
                                origin: nextPosition.origin ?? "top-left",
                            },
                        };
                    }
                    return { ...qr, position: nextPosition };
                }),
            };
        });
    };

    const updateQrSize = (id: string, size: number) => {
        setMeta((current) => {
            if (!current) return current;
            return {
                ...current,
                qrCodes: current.qrCodes.map((qr) => {
                    if (qr.id !== id) return qr;
                    const normalizedSize = normalizeQrSize(size);
                    if (qr.position.type === "coordinates") {
                        const clamped = clampQrPosition(qr.position.page, normalizedSize, {
                            x: qr.position.x,
                            y: qr.position.y,
                        });
                        return {
                            ...qr,
                            size: normalizedSize,
                            position: {
                                ...qr.position,
                                x: clamped.x,
                                y: clamped.y,
                                origin: qr.position.origin ?? "top-left",
                            },
                        };
                    }
                    return { ...qr, size: normalizedSize };
                }),
            };
        });
    };

    const handleQrSizeInputChange = (id: string, value: string) => {
        setQrSizeDrafts((current) => ({ ...current, [id]: value }));
    };

    const commitQrSize = (id: string, fallbackSize: number) => {
        setQrSizeDrafts((current) => {
            const next = { ...current };
            delete next[id];
            return next;
        });

        const raw = qrSizeDrafts[id];
        const parsed = raw == null || raw.trim() === "" ? fallbackSize : Number(raw);
        updateQrSize(id, parsed);
    };

    const handleAddQr = () => {
        setMeta((current) => {
            if (!current) return current;
            const next = createDefaultQr();
            return { ...current, qrCodes: [...current.qrCodes, next] };
        });
    };

    const handleRemoveQr = (id: string) => {
        setMeta((current) => {
            if (!current) return current;
            return { ...current, qrCodes: current.qrCodes.filter((qr) => qr.id !== id) };
        });
        if (selectedQrId === id) {
            setSelectedQrId(null);
        }
    };

    const currentSnapshot = React.useMemo(() => (meta ? getMetaSnapshot(meta) : null), [meta]);
    const isDirty = Boolean(meta && savedSnapshot && currentSnapshot !== savedSnapshot);

    const handleSave = async () => {
        if (!meta) return;
        const previousSnapshot = savedSnapshot;
        const optimisticSnapshot = getMetaSnapshot(meta);
        setSavedSnapshot(optimisticSnapshot);
        try {
            const updatedMeta = await apiService.saveTemplateMeta(meta.id, meta);
            setMeta(updatedMeta);
            setSavedSnapshot(getMetaSnapshot(updatedMeta));
            setTemplate((current) => (current ? { ...current, name: updatedMeta.name } : current));
            setSuccessToastMessage(t("editor.save.success"));
        } catch (err) {
            logger.error("Failed to save template metadata", err);
            setSavedSnapshot(previousSnapshot);
            setErrorToastMessage(t("editor.save.error"));
        }
    };

    const selectedTabContent = () => {
        if (!meta) {
            return null;
        }

        if (activeTab === "fields") {
            return (
                <div className="template-editor-fields">
                    {meta.fields.map((field) => (
                        <div
                            key={field.name}
                            className={`template-editor-field-card ${
                                selectedFieldName === field.name
                                    ? "is-selected"
                                    : hoveredFieldName === field.name
                                      ? "is-hovered"
                                      : ""
                            }`}
                            ref={(el) => {
                                fieldCardRefs.current[field.name] = el;
                            }}
                            onMouseEnter={() => setHoveredFieldName(field.name)}
                            onMouseLeave={() => setHoveredFieldName(null)}
                        >
                            <div className="form-section-title">{field.name}</div>
                            {pageCount > 1 && (
                                <div className="template-editor-field-page">
                                    {t("editor.fields.pageLabel", {
                                        pages: getFieldPages(field.name).join(", "),
                                    })}
                                </div>
                            )}
                            <Form.Group className="template-editor-field-input">
                                <Form.Label className="form-label-overline">{t("editor.fields.alternativeLabel")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    size="sm"
                                    value={field.alternativeName ?? ""}
                                    onChange={(event) =>
                                        updateField(field.name, { alternativeName: event.target.value })
                                    }
                                    onFocus={() => setSelectedFieldName(field.name)}
                                />
                            </Form.Group>
                            <Form.Group className="template-editor-field-input">
                                <Form.Label className="form-label-overline">{t("editor.fields.defaultLabel")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    size="sm"
                                    value={field.defaultValue ?? ""}
                                    onChange={(event) => updateField(field.name, { defaultValue: event.target.value })}
                                    onFocus={() => setSelectedFieldName(field.name)}
                                />
                            </Form.Group>
                        </div>
                    ))}
                </div>
            );
        }

        if (activeTab === "info") {
            return (
                <div className="template-editor-fields">
                    <div className="template-editor-field-card">
                        <div className="form-section-title">{t("editor.info.title")}</div>
                        <Form.Group className="template-editor-name">
                            <Form.Label className="form-label-overline">{t("editor.name.label")}</Form.Label>
                            <Form.Control
                                type="text"
                                size="sm"
                                value={meta.name}
                                onChange={(event) => updateTemplateName(event.target.value)}
                            />
                        </Form.Group>
                    </div>
                </div>
            );
        }

        return (
            <div className="template-editor-qr-list">
                {meta.qrCodes.length === 0 && (
                    <div className="text-muted">{t("editor.qr.empty")}</div>
                )}
                {meta.qrCodes.map((qr) => (
                    (() => {
                        const allowSelectionHighlight = meta.qrCodes.length > 1;
                        return (
                    <div
                        key={qr.id}
                        className={`template-editor-qr-card ${
                            allowSelectionHighlight && selectedQrId === qr.id ? "is-selected" : ""
                        }`}
                        onClick={() => {
                            if (allowSelectionHighlight) {
                                setSelectedQrId(qr.id);
                            } else {
                                setSelectedQrId(null);
                            }
                        }}
                    >
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="form-section-title">{t("editor.qr.itemTitle")}</div>
                            <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    handleRemoveQr(qr.id);
                                }}
                            >
                                {t("editor.qr.remove")}
                            </Button>
                        </div>
                        <Form.Group className="mt-2">
                            <Form.Label className="form-label-overline">{t("editor.qr.content")}</Form.Label>
                            <Form.Control
                                type="text"
                                value={qr.content}
                                onChange={(event) => updateQr(qr.id, { content: event.target.value })}
                            />
                        </Form.Group>
                        <div className="template-editor-qr-grid">
                            <Form.Group>
                                <Form.Label className="form-label-overline">{t("editor.qr.color")}</Form.Label>
                                <Form.Control
                                    type="color"
                                    value={qr.color ?? "#000000"}
                                    onChange={(event) => updateQr(qr.id, { color: event.target.value })}
                                />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label className="form-label-overline">{t("editor.qr.backgroundColor")}</Form.Label>
                                <Form.Control
                                    type="color"
                                    value={qr.backgroundColor ?? "#ffffff"}
                                    onChange={(event) => updateQr(qr.id, { backgroundColor: event.target.value })}
                                />
                            </Form.Group>
                        </div>
                        <div className="template-editor-qr-grid">
                            <Form.Group>
                                <Form.Label className="form-label-overline">{t("editor.qr.page")}</Form.Label>
                                <Form.Select
                                    value={qr.position.page}
                                    onChange={(event) =>
                                        updateQrPosition(qr.id, { page: Number(event.target.value) })
                                    }
                                >
                                    {Array.from({ length: pageCount }, (_, idx) => idx + 1).map((page) => (
                                        <option key={page} value={page}>
                                            {page}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group>
                                <Form.Label className="form-label-overline">{t("editor.qr.origin")}</Form.Label>
                                <Form.Select
                                    value={qr.position.origin ?? "top-left"}
                                    onChange={(event) =>
                                        updateQrPosition(qr.id, { origin: event.target.value as any })
                                    }
                                >
                                    <option value="top-left">{t("editor.qr.originTopLeft")}</option>
                                    <option value="top-right">{t("editor.qr.originTopRight")}</option>
                                    <option value="bottom-left">{t("editor.qr.originBottomLeft")}</option>
                                    <option value="bottom-right">{t("editor.qr.originBottomRight")}</option>
                                </Form.Select>
                            </Form.Group>
                        </div>
                        <div className="template-editor-qr-grid">
                            <Form.Group>
                                <Form.Label className="form-label-overline">{t("editor.qr.x")}</Form.Label>
                            <Form.Control
                                type="number"
                                value={qr.position.x}
                                onChange={(event) => {
                                    const value = Number(event.target.value);
                                    if (!Number.isFinite(value)) {
                                        return;
                                    }
                                    updateQrPosition(qr.id, { x: value });
                                }}
                            />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label className="form-label-overline">{t("editor.qr.y")}</Form.Label>
                            <Form.Control
                                type="number"
                                value={qr.position.y}
                                onChange={(event) => {
                                    const value = Number(event.target.value);
                                    if (!Number.isFinite(value)) {
                                        return;
                                    }
                                    updateQrPosition(qr.id, { y: value });
                                }}
                            />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label className="form-label-overline">{t("editor.qr.size")}</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={qrSizeDrafts[qr.id] ?? String(qr.size ?? 120)}
                                    min={MIN_QR_SIZE}
                                    max={MAX_QR_SIZE}
                                    onChange={(event) => handleQrSizeInputChange(qr.id, event.target.value)}
                                    onBlur={() => commitQrSize(qr.id, qr.size ?? 120)}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter") {
                                            commitQrSize(qr.id, qr.size ?? 120);
                                        }
                                    }}
                                />
                            </Form.Group>
                        </div>
                    </div>
                        );
                    })()
                ))}
                <div className="template-editor-qr-footer">
                    <Button size="sm" variant="outline-primary" onClick={handleAddQr}>
                        {t("editor.qr.add")}
                    </Button>
                </div>
            </div>
        );
    };

    return (
        <div className="template-editor">
            <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                    <Button
                        variant="link"
                        className="template-editor-backlink"
                        onClick={() => navigate("/templates")}
                    >
                        {t("editor.actions.back")}
                    </Button>
                    <h1 className="display-6 mb-1">{t("editor.title")}</h1>
                    {meta ? (
                        <div className="text-muted">{meta.name}</div>
                    ) : (
                        template && <div className="text-muted">{template.name}</div>
                    )}
                </div>
                <div className="d-flex flex-column align-items-end gap-1">
                    <div className="d-flex align-items-center gap-2">
                        <Button
                            as="a"
                            variant="outline-secondary"
                            href={templatePdfBlobUrl ?? undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            role="button"
                            disabled={!templatePdfBlobUrl}
                        >
                            {t("pdfPreview:actions.open")}
                        </Button>
                        <span title={isDirty ? t("editor.save.unsavedHint") : t("editor.save.savedHint")}>
                            <Button variant="primary" onClick={handleSave} disabled={!meta || !isDirty}>
                                {t("editor.actions.save")}
                            </Button>
                        </span>
                    </div>
                    <div
                        className={`template-editor-save-status ${
                            isDirty ? "is-dirty" : "is-clean"
                        }`}
                    >
                        {isDirty ? t("editor.save.unsavedHint") : t("editor.save.savedHint")}
                    </div>
                </div>
            </div>

            {isLoading && (
                <div className="d-flex gap-2 align-items-center mb-3">
                    <Spinner animation="border" role="status" size="sm" />
                    <span>{t("editor.loading")}</span>
                </div>
            )}

            {error && (
                <Alert variant="danger" className="mb-3">
                    {error}
                </Alert>
            )}

            {template && meta && (
                <Row className="g-4 flex-fill template-editor-layout">
                    <Col lg={8}>
                        <div
                            className="template-editor-preview-card"
                            onClick={() => {
                                setSelectedQrId(null);
                                setSelectedFieldName(null);
                            }}
                        >
                            <div className="template-editor-preview-toolbar">
                                <Form.Check
                                    type="switch"
                                    id="show-fields"
                                    label={t("editor.preview.showFields")}
                                    checked={showFieldNames}
                                    onChange={(event) => setShowFieldNames(event.target.checked)}
                                />
                                <Form.Check
                                    type="switch"
                                    id="show-qr"
                                    label={t("editor.preview.showQr")}
                                    checked={showQrCodes}
                                    onChange={(event) => setShowQrCodes(event.target.checked)}
                                />
                                <Form.Select
                                    size="sm"
                                    className="template-editor-zoom"
                                    value={previewScale}
                                    onChange={(event) => setPreviewScale(Number(event.target.value))}
                                >
                                    <option value={0.75}>{t("editor.preview.zoomSmall")}</option>
                                    <option value={1}>{t("editor.preview.zoomNormal")}</option>
                                    <option value={1.25}>{t("editor.preview.zoomLarge")}</option>
                                </Form.Select>
                            </div>
                            <TemplatePreviewPane
                                pdfBytes={pdfBytes}
                                fieldWidgets={fieldWidgets}
                                fieldMeta={meta.fields}
                                qrCodes={meta.qrCodes}
                                showFieldNames={showFieldNames}
                                showQrCodes={showQrCodes}
                                selectedQrId={selectedQrId}
                                selectedFieldName={selectedFieldName}
                                hoveredFieldName={hoveredFieldName}
                                scale={previewScale}
                                onPageCount={(count) => setPageCount(count)}
                                onPageMetrics={handlePageMetrics}
                                onSelectQr={(qrId) => {
                                    if (meta.qrCodes.length > 1) {
                                        setSelectedQrId(qrId);
                                    } else {
                                        setSelectedQrId(null);
                                    }
                                    setActiveTab("qr");
                                }}
                                onQrPlacementChange={updateQrFromDrag}
                                allowSelectionHighlight={meta.qrCodes.length > 1}
                                onSelectField={(fieldName) => {
                                    setSelectedFieldName(fieldName);
                                    setActiveTab("fields");
                                    const card = fieldCardRefs.current[fieldName];
                                    if (card) {
                                        card.scrollIntoView({ behavior: "smooth", block: "nearest" });
                                        const input = card.querySelector("input");
                                        if (input instanceof HTMLInputElement) {
                                            input.focus();
                                        }
                                    }
                                }}
                                onHoverField={(fieldName) => setHoveredFieldName(fieldName)}
                            />
                        </div>
                    </Col>
                    <Col lg={4}>
                        <div className="template-editor-panel" onClick={() => { setSelectedFieldName(null); setSelectedQrId(null); }}>
                            <Nav variant="tabs" activeKey={activeTab} onSelect={(eventKey) => {
                                if (eventKey === "fields" || eventKey === "qr" || eventKey === "info") {
                                    setActiveTab(eventKey);
                                }
                            }}>
                                <Nav.Item>
                                    <Nav.Link eventKey="fields">{t("editor.tabs.fields")}</Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="qr">{t("editor.tabs.qr")}</Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="info">{t("editor.tabs.info")}</Nav.Link>
                                </Nav.Item>
                            </Nav>
                            <div className="template-editor-panel-body">{selectedTabContent()}</div>
                        </div>
                    </Col>
                </Row>
            )}
            <ToastSuccess
                show={successToastMessage !== null}
                message={successToastMessage || ""}
                onClose={() => setSuccessToastMessage(null)}
            />
            <ToastWarning
                show={errorToastMessage !== null}
                message={errorToastMessage || ""}
                onClose={() => setErrorToastMessage(null)}
            />
        </div>
    );
};

export default TemplateEditor;
